import { Box, Divider, Grid2, Stack, Typography } from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import ApexCharts from "apexcharts";

import ChartCalendar from "../../../ChartCalendar";
import CartLoader from "../../../../../Helper/Loaders/CartLoader";
import VitalsChartDateCarousel from "../VitalsChartDateCarousel";
import ComparisonVitalSelection from "./ComparisonVitalSelection";
import VitalsComparisonChart from "./VitalsComparisonChart";
import RenderChartLegend from "./RenderChartLegend";
import { useAbortController } from "../../../../../Hooks/useAbortController";
import { applyColorsToContinuousSignals, prepareContinuousSignalsData, calculateCarouselData, getBatterySignalsData, getDigitalTwinChartDataForComparison, shouldDisableGranularity } from "../../../../../Helper/DigitalTwin/DigitalTwinFunctions";
import { PrimaryGranularityButton, SecondaryGranularityButton } from "../../../../../Theme/DigitalTwinStyles";
import CustomIcon from "../../../../../Components/CustomIcon";
import { PrimaryTooltip } from "../../../../../Components/StyledComponent";
import ZoomResetView from "../../../components/ZoomResetView";
import { axisColors, primaryGranularities, secondaryGranularitiesForComparison } from '../../../../../Helper/DigitalTwin/DataFile';
import { getDatePickerDate, getLocalDateFromUTC } from "../../../../../Helper/DatePicker/DateConverter";
import { useBatteryInfo } from "../../../../../Pages/DigitalTwinV3/DigitalTwinContext";
import Cookies from "universal-cookie";
import moment from "moment";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { calculateIntervalFromTimeWindow, formatChartTimestampForApi } from "../../../../../Helper/Charts/ChartHelper";
import ChartActions from "../ChartActions";
import { getCurrentTimestamp } from "../../../../../Helper/DatePicker/DateConverter";
import { usePagesContent } from "../../../../../Hooks/usePagesContent";

dayjs.extend(utc);
dayjs.extend(timezone);


export default function ComparisonChartSection({
    batteryInfo,
    batteryID,
    selectedTab,
    selectedNodes,
    dataGapDates,
    showBusDeviceInfo = () => {},
    showDownloadButton = true,
    onDownloadCSV = () => {},
    downloadDisabled = false,
    onExportDataChange = () => {},
}) {

    const { t } = useTranslation(['common', 'digital_twin'])
    const pageContent = usePagesContent();
    const MAX_METRICS = pageContent?.metaInfo?.globalConfig?.["multipartCompare.maxSignals"] || 4;
    const currentDatesRedux = useSelector((state) => state.digitalTwin.currentDates);
    const [currentDates, setCurrentDates] = React.useState(currentDatesRedux)
    const zoomResetViewRef = React.useRef(null);
    const skipSecondaryGranularityEffectRef = React.useRef(false);
    const [primaryGranularity, setPrimaryGranularity] = React.useState('raw')
    const [secondaryGranularity, setSecondaryGranularity] = React.useState('avg')
    const [allContinuousSignals, setAllContinuousSignals] = React.useState([]);
    const [selectedContinuousSignals, setSelectedContinuousSignals] = React.useState([])
    const [legendSeries, setLegendSeries] = React.useState([])
    const [metricsSeries, setMetricsSeries] = React.useState({})
    const [selectedDate, setSelectedDate] = React.useState({ key: 0, value: currentDates.endDate })
    const [isChartLoading, setIsChartLoading] = React.useState(true)
    const [categories, setCategories] = React.useState([])
    const [showPrimaryGranularities, setShowPrimaryGranularities] = React.useState(false);
    const [showSecondaryGranularities, setShowSecondaryGranularities] = React.useState(false);
    const [intervalSeconds, setIntervalSeconds] = React.useState(300);
    const [isZoomed, setIsZoomed] = React.useState(false);
    const [zoomedRange, setZoomedRange] = React.useState(null);
    const zoomTimeoutRef = React.useRef(null);
    const chartID = 'compare-vitals-chart' + secondaryGranularity;
    const chartIDs = Array.from({ length: MAX_METRICS }, (_, i) => `${chartID}${i}`);
    const { getSignal, isCurrentRequest } = useAbortController()

    const {batteryInfo: batteryInfoData} = useBatteryInfo();
    let addedOn = batteryInfoData?.addedOn;

    const cookies = new Cookies();
    const timeZone = cookies.get("timeZone");
    if (timeZone) {
        addedOn = moment?.utc(addedOn, "YYYY-MM-DD HH:mm:ss")?.tz(timeZone)?.format("YYYY-MM-DD");
    } else {
        addedOn = moment?.utc(addedOn, "YYYY-MM-DD HH:mm:ss")?.format("YYYY-MM-DD");
    }

    const isRawGranularity = primaryGranularity === 'raw'

    const handleDateChange = ({ startDate, endDate }) => {
        setCurrentDates({ startDate, endDate })
    }

    const onPrimaryGranularityChange = (granularity) => {
        if (granularity === primaryGranularity) {
            return false
        }
        setIsChartLoading(true)
        setPrimaryGranularity(granularity)
        if (granularity == 'raw') {
            // Setting secondaryGranularity here batches with primaryGranularity
            // and fires both [primaryGranularity] and [secondaryGranularity] effects.
            // The [secondaryGranularity] effect reads a stale selectedDate (wrong
            // format for the new granularity) and overwrites the correct date set
            // by [primaryGranularity]. Skip it so only [primaryGranularity] runs.
            skipSecondaryGranularityEffectRef.current = true;
            setSecondaryGranularity('avg')
        }
    }

    const onSecondaryGranularityChange = (granularity) => {
        if (granularity === secondaryGranularity) {
            return false
        }
        setIsChartLoading(true)
        setSecondaryGranularity(granularity)
    }

    const applySelectedContinuousSignals = (items) => {
        setSelectedContinuousSignals(applyColorsToContinuousSignals(items))
    }

    const exportChartsToOneSVG = async (targetChartIds, chartConfig = {}, fileName) => {
        const parser = new DOMParser();
        const serializer = new XMLSerializer();
        const combinedSVG = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        combinedSVG.setAttribute("xmlns", "http://www.w3.org/2000/svg");
        combinedSVG.setAttribute("version", "1.1");

        let yOffset = 0;
        let totalWidth = 0;
        const chartSpacing = 30;
        const titleSpacing = 40;
        const legendSpacing = 20;
        const headerSpacing = titleSpacing + legendSpacing;

        const mergedColors = {};
        targetChartIds.forEach((id) => {
            const cfg = chartConfig[id] || {};
            Object.entries(cfg.colors || {}).forEach(([label, color]) => {
                if (!mergedColors[label]) {
                    mergedColors[label] = color;
                }
            });
        });

        if (Object.keys(mergedColors).length > 0) {
            let legendX = 0;
            const legendY = 32;
            Object.entries(mergedColors).forEach(([label, color]) => {
                const group = document.createElementNS("http://www.w3.org/2000/svg", "g");

                const circle = document.createElementNS("http://www.w3.org/2000/svg", "circle");
                circle.setAttribute("cx", legendX + 6);
                circle.setAttribute("cy", legendY);
                circle.setAttribute("r", 8);
                circle.setAttribute("fill", color);
                group.appendChild(circle);

                const text = document.createElementNS("http://www.w3.org/2000/svg", "text");
                text.textContent = label;
                text.setAttribute("x", legendX + 16);
                text.setAttribute("y", legendY + 4);
                text.setAttribute("font-size", "14");
                text.setAttribute("fill", "#000");
                group.appendChild(text);

                combinedSVG.appendChild(group);
                legendX += label.length * 5 + 70;
            });
        }

        for (const id of targetChartIds) {
            const cfg = chartConfig[id] || {};
            const chartTitle = cfg.title || `Chart: ${id}`;
            const chartInstance = ApexCharts.getChartByID(id);
            if (!chartInstance) {
                continue;
            }

            const svgString = await chartInstance.getSvgString();
            if (!svgString) {
                continue;
            }

            const doc = parser.parseFromString(svgString, "image/svg+xml");
            const svgEl = doc.documentElement;
            const width = parseFloat(svgEl.getAttribute("width")) || 800;
            const height = parseFloat(svgEl.getAttribute("height")) || 300;
            totalWidth = Math.max(totalWidth, width);

            const titleText = document.createElementNS("http://www.w3.org/2000/svg", "text");
            titleText.textContent = chartTitle;
            titleText.setAttribute("x", 0);
            titleText.setAttribute("y", yOffset + headerSpacing + 32);
            titleText.setAttribute("font-size", "14");
            titleText.setAttribute("font-weight", "600");
            titleText.setAttribute("fill", "#000");
            combinedSVG.appendChild(titleText);

            const g = document.createElementNS("http://www.w3.org/2000/svg", "g");
            g.setAttribute("transform", `translate(0, ${yOffset + headerSpacing + titleSpacing + legendSpacing + 20})`);
            while (svgEl.firstChild) {
                g.appendChild(svgEl.firstChild);
            }
            combinedSVG.appendChild(g);

            yOffset += height + chartSpacing + titleSpacing + legendSpacing;
        }

        combinedSVG.setAttribute("width", totalWidth);
        combinedSVG.setAttribute("height", yOffset + headerSpacing);

        const blob = new Blob([serializer.serializeToString(combinedSVG)], { type: "image/svg+xml" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName || "comparison-charts.svg";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const downloadComparisonImage = async () => {
        try {
            const timestamp = getCurrentTimestamp("YYYY_MM_DD_HH_mm_ss");
            const fileName = `${timestamp}_BatteryVitals_Compare.svg`;
            const chartConfig = {};
            selectedContinuousSignals.forEach((metric, index) => {
                const seriesList = metricsSeries[metric.key] || [];
                const colors = {};
                seriesList.forEach((series) => {
                    if (series?.name && series?.color) {
                        colors[series.name] = series.color;
                    }
                });
                chartConfig[chartIDs[index]] = {
                    title: metric.label || metric.name || metric.key || `Chart ${index + 1}`,
                    colors,
                };
            });
            await exportChartsToOneSVG(chartIDs, chartConfig, fileName);
        } catch (error) {
            console.error('Error downloading chart:', error);
        }
    };

    const fetchChartData = async ({ startDate, endDate, intervalSeconds }) => {
        if (!selectedNodes || selectedNodes.length < 1 || !selectedContinuousSignals || selectedContinuousSignals.length < 1) {
            setIsChartLoading(false)
            onExportDataChange({
                metricsSeries: {},
                selectedMetrics: selectedContinuousSignals,
                selectedNodes: selectedNodes,
                primaryGranularity,
                secondaryGranularity,
                isLoaded: false,
            })
            return false
        }

        setIsChartLoading(true)
        onExportDataChange({
            metricsSeries: {},
            selectedMetrics: selectedContinuousSignals,
            selectedNodes: selectedNodes,
            primaryGranularity,
            secondaryGranularity,
            isLoaded: false,
        })

        const { signal, requestId } = getSignal()

        try {
            const [seriesData, metricsSeriesData, categories] = await getDigitalTwinChartDataForComparison({
                batteryID: batteryID,
                startDate: startDate,
                endDate: endDate,
                selectedDate: selectedDate.value,
                primaryGranularity: primaryGranularity,
                secondaryGranularity: secondaryGranularity,
                selectedNodes: selectedNodes,
                selectedMetrics: selectedContinuousSignals,
                metricKeys: selectedContinuousSignals.map(metric => metric.key),
                signal: signal,
                intervalSeconds: intervalSeconds
            })

            if (!isCurrentRequest(requestId)) {
                return;
            }

            setLegendSeries(seriesData)
            setMetricsSeries(metricsSeriesData)
            setSeriesForLegends(metricsSeriesData)
            setCategories(categories)
            setIsChartLoading(false)
            onExportDataChange({
                metricsSeries: metricsSeriesData,
                selectedMetrics: selectedContinuousSignals,
                selectedNodes: selectedNodes,
                primaryGranularity,
                secondaryGranularity,
                isLoaded: true,
            })
        } catch (error) {
            if (!isCurrentRequest(requestId) && error.name === 'AbortError') {
                return;
            }
            console.log(error)
            setIsChartLoading(false)
            onExportDataChange({
                metricsSeries: {},
                selectedMetrics: selectedContinuousSignals,
                selectedNodes: selectedNodes,
                primaryGranularity,
                secondaryGranularity,
                isLoaded: false,
            })
        }
    }

    const resetChartData = async (overrideIntervalSeconds = null) => {
        const intervalToUse = overrideIntervalSeconds !== null ? overrideIntervalSeconds : intervalSeconds;

        await fetchChartData({
            startDate: currentDates.startDate,
            endDate: currentDates.endDate,
            intervalSeconds: intervalToUse
        });
    }

    const changeSelectedDateFromList = (date) => {
        // Reset zoom state when changing dates
        setIsZoomed(false);
        setZoomedRange(null);
        setIntervalSeconds(300); // Reset to default interval
        setSelectedDate({ key: selectedDate.key + 1, value: date })
    }

    const resetChartDataAfterZoom = (xaxis) => {
        // Clear any existing timeout
        if (zoomTimeoutRef.current) {
            clearTimeout(zoomTimeoutRef.current);
        }

        // Debounce the zoom update to prevent multiple rapid calls
        zoomTimeoutRef.current = setTimeout(async () => {

            // Use the xaxis parameter from beforeZoom event
            const minX = xaxis?.min;
            const maxX = xaxis?.max;

            if (!minX || !maxX) {
                setIsChartLoading(false);
                return;
            }

            // Calculate the appropriate interval based on the visible time window
            const calculatedInterval = calculateIntervalFromTimeWindow(minX, maxX);

            // Convert minX and maxX timestamps to date strings for the API
            const zoomedStartDate = formatChartTimestampForApi(minX);
            const zoomedEndDate = formatChartTimestampForApi(maxX);

            // Store zoom state
            setIsZoomed(true);
            setZoomedRange({ minX, maxX });
            setIntervalSeconds(calculatedInterval);

            // Fetch new data with the zoomed range
            await fetchChartData({
                startDate: zoomedStartDate,
                endDate: zoomedEndDate,
                intervalSeconds: calculatedInterval
            });
        }, 500); // 500ms debounce
    }

    const handleZoomReset = async () => {
        // Reset interval to default (300 seconds / 5 minutes)
        const defaultInterval = 300;
        setIntervalSeconds(defaultInterval);

        // Reset zoom state
        setIsZoomed(false);
        setZoomedRange(null);

        // Refetch chart data with original date range and default interval
        await resetChartData(defaultInterval);
    }

    const shouldDisableSecondaryGranularity = (granularity) => {
        if (isRawGranularity) {
            return true
        }

        return false
    }

    const resetChartToLatestData = () => {
        if (!batteryInfo.lastPingedTime) {
            alert("No data available for selected date")
        }

        let lastPingDate = getLocalDateFromUTC(batteryInfo.lastPingedTime)
        let startDate2 = getDatePickerDate(lastPingDate, 0)
        let endDate2 = getDatePickerDate(lastPingDate, 0)

        handleDateChange({ startDate: startDate2, endDate: endDate2 })
    }

    const setSeriesForLegends = (newMetricsSeries) => {
        let metric = selectedContinuousSignals[0]
        let seriesData = (newMetricsSeries[metric.key] || []).filter(s => s.type !== 'rangeArea')
        setLegendSeries(seriesData)
    }

    const setupInitialMetrics = async () => {
        if (!selectedTab) {
            return null
        }

        const { continuousSignals } = await getBatterySignalsData(batteryID, selectedTab);

        const { formattedContinuousSignals, defaultSelectedContinuousSignals } = prepareContinuousSignalsData(continuousSignals, selectedTab, MAX_METRICS);
        setSelectedContinuousSignals(defaultSelectedContinuousSignals);
        setAllContinuousSignals(formattedContinuousSignals);
    };


    React.useEffect(() => {
        // Clear stale signals immediately so any fetch triggered by [selectedDate, selectedNodes]
        // before setupInitialMetrics completes hits the guard and returns early.
        setSelectedContinuousSignals([]);
        setupInitialMetrics()
    }, [selectedTab, batteryID])

    React.useEffect(() => {
        setPrimaryGranularity('raw');
        setSecondaryGranularity('avg');
        const selectedDateValue = calculateCarouselData(currentDates.startDate, currentDates.endDate, 'raw')[0]
        changeSelectedDateFromList(selectedDateValue)
    }, [currentDates]);

    React.useEffect(() => {
        const selectedDateValue = calculateCarouselData(currentDates.startDate, currentDates.endDate, primaryGranularity)[0]
        changeSelectedDateFromList(selectedDateValue);
    }, [primaryGranularity]);

    React.useEffect(() => {
        // When primaryGranularity also changed in the same batch (e.g. switching to
        // raw resets secondaryGranularity to avg), the [primaryGranularity] effect
        // already calculates the correct date. Skip here to avoid overwriting it
        // with a stale selectedDate value in the wrong format.
        if (skipSecondaryGranularityEffectRef.current) {
            skipSecondaryGranularityEffectRef.current = false;
            return;
        }

        let selectedDateValue = null;

        if (selectedDate && selectedDate.value) {
            selectedDateValue = selectedDate.value
        }

        if (!selectedDateValue) {
            selectedDateValue = calculateCarouselData(currentDates.startDate, currentDates.endDate, primaryGranularity)[0]
        }

        changeSelectedDateFromList(selectedDateValue)
    }, [secondaryGranularity, selectedContinuousSignals]);

    React.useEffect(() => {
        resetChartData();
    }, [selectedDate, selectedNodes]);

    React.useEffect(() => {
        const isChartGranularityEnabled = pageContent?.metaInfo?.globalConfig?.["isChartGranularityEnabled"] !== false;
        if (isChartGranularityEnabled) {
            setShowPrimaryGranularities(true);
            setShowSecondaryGranularities(true);
        }
    }, [batteryID])

    if (!selectedNodes || selectedNodes.length <= 0) {
        return <CartLoader />;
    }

    const todayDate = moment().tz(timeZone).format("YYYY-MM-DD");

    return (
        <Grid2>
            <Grid2
                container
                gap={'16px'}
                justifyContent={'space-between'}
                className="comparison-chart-section-container"
                sx={{ pl: '20px', pr: '35px', py: '16px', pb: '0px' }}
            >
                <Grid2 container gap={'8px'}>
                    <ChartCalendar
                        key={'comparison_section_calender'}
                        startDate={currentDates.startDate}
                        endDate={currentDates.endDate}
                        onDateChange={handleDateChange}
                        dataGapDates={dataGapDates}
                        disableBeforeDate={addedOn}
                        disableAfterDate={todayDate}
                    />

                    {showPrimaryGranularities && <Grid2 container sx={{ background: '#F9F9F9', padding: '4px', borderRadius: '6px' }}>
                        {primaryGranularities.map(granularity => (
                            <PrimaryGranularityButton
                                key={granularity}
                                disabled={shouldDisableGranularity(granularity, currentDates.startDate, currentDates.endDate)}
                                onClick={() => onPrimaryGranularityChange(granularity)}
                                active={primaryGranularity === granularity}>
                                <Typography textTransform="capitalize">{granularity}</Typography>
                            </PrimaryGranularityButton>
                        ))}
                    </Grid2>}
                    {showSecondaryGranularities && <Grid2 container sx={{ background: '#F9F9F9', padding: '4px', borderRadius: '6px' }}>
                        {secondaryGranularitiesForComparison.map(granularity => (
                            <PrimaryTooltip
                                key={granularity}
                                title={!isRawGranularity ? (granularity === 'min-avg-max' ? 'Min, Max & Avg. Values' : 'Avg. Values') : undefined}
                                placement="bottom"
                            >
                                <SecondaryGranularityButton
                                    disabled={shouldDisableSecondaryGranularity(granularity)}
                                    onClick={() => onSecondaryGranularityChange(granularity)}
                                    active={!isRawGranularity && secondaryGranularity === granularity}>
                                    <CustomIcon height="12px" name={granularity === 'avg' ? 'trend-line-icon' : 'filled-trend-line-icon'} />
                                </SecondaryGranularityButton>
                            </PrimaryTooltip>
                        ))}
                    </Grid2>}
                </Grid2>

                <Grid2 container alignItems="center" justifyContent="space-between" gap={2}>
                    <ComparisonVitalSelection
                        itemList={allContinuousSignals}
                        selectedItems={selectedContinuousSignals}
                        applySelectedItems={applySelectedContinuousSignals}
                        batteryID={batteryID}
                    />
                    {showDownloadButton && (
                        <Box sx={{ ml: 'auto' }}>
                            <ChartActions actions={[
                                { type: 'nothing', label: 'Download Comparison Data', icon: 'download-file-icon', onClick: onDownloadCSV },
                                { type: 'nothing', label: 'Download Comparison Image', icon: 'download-image-icon', onClick: downloadComparisonImage },
                            ]} />
                        </Box>
                    )}
                </Grid2>
            </Grid2>

            <Box sx={{pr: 2}}>
                <Grid2 container alignItems={'center'} gap={'12px'} sx={{ px: 7.5, my: 2, mb: 0 }}>
                    <RenderChartLegend chartIDs={chartIDs} legendSeries={legendSeries} isLoading={isChartLoading} />
                </Grid2>
            </Box>

            <Box sx={{
                marginBottom: '0px',
                height: 'calc(100vh - 26vh)',
                overflowY: 'auto',
                overflowX: 'hidden',
                px: '16px',
            }}>
                <ZoomResetView ref={zoomResetViewRef} chartID={chartIDs} onReset={handleZoomReset} isZoomedProp={isZoomed} />
                {
                    selectedContinuousSignals.length > 0 && selectedContinuousSignals.map((selectedMetric, index) => {
                        return (
                            <Box >
                                <Box sx={{
                                    maxHeight: '237px',
                                    paddingRight: 0,
                                    '& .comparison-tooltip-container': { zIndex: 999999 }
                                }}
                                >
                                    <VitalsComparisonChart
                                        key={`${selectedMetric.name}-${chartIDs[index]}-${Math.random()}`}
                                        isLoading={isChartLoading}
                                        chartID={chartIDs[index]}
                                        chartKey={Math.random()}
                                        metrics={selectedContinuousSignals}
                                        series={metricsSeries[selectedMetric.key] || []}
                                        categories={categories}
                                        axisColors={axisColors}
                                        startDate={selectedDate}
                                        currentMetric={selectedMetric}
                                        primaryGranularity={primaryGranularity}
                                        zoomResetViewRef={zoomResetViewRef}
                                        resetToLatestDataDate={() => resetChartToLatestData()}
                                        onZoom={resetChartDataAfterZoom}
                                        onZoomReset={handleZoomReset}
                                        zoomedRange={zoomedRange}
                                        setLoading={setIsChartLoading}
                                        isZoomed={isZoomed}
                                    />
                                </Box>
                                <Divider sx={{ marginLeft: -2, marginRight: -2, mt: 3, mb: 2 }} />
                            </Box>
                        )
                    })
                }
            </Box>

            <Box sx={{
                display: 'flex',
                backgroundColor: '#fff',
                alignItems: 'center',
                position: 'absolute',
                bottom: '0px',
                zIndex: 99999,
            }}>
                <Box sx={{ width: 'calc(100vw - 34vw)', px: '60px' }}>
                    <VitalsChartDateCarousel
                        primaryGranularity={primaryGranularity}
                        currentDates={currentDates}
                        onChangeDate={(date) => changeSelectedDateFromList(date)}
                        dataGapDates={dataGapDates}
                    />
                </Box>
            </Box>
        </Grid2 >
    )
}
